import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../context/AuthContext';
import { updateProfile } from '../../api/authAPI';
import {
  getWorkerProfile as fetchWorkerProfile,
  updateWorkerProfile as updateWorkerCareer,
} from '../../api/workerAPI';
import { useTheme } from '../../context/ThemeContext/ThemeContext';
import { FileText, LogOut, Edit3, Star, User, Settings, Briefcase, Mail, Phone, MapPin, Calendar, Shield, ChevronRight, Award, Eye, Send } from 'lucide-react';


const containerVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.05 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 15 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, ease: [0.16, 1, 0.3, 1] },
  },
};

const buildFormData = (sourceUser) => ({
  name: sourceUser?.name || '',
  phone: sourceUser?.phone || '',
  profileImage: sourceUser?.profileImage || '',
  city: sourceUser?.location?.city || '',
  state: sourceUser?.location?.state || '',
  pincode: sourceUser?.location?.pincode || '',
  skills: Array.isArray(sourceUser?.skills)
    ? sourceUser.skills.join(', ')
    : sourceUser?.skills || '',
  bio: sourceUser?.bio || '',
  isAvailable: sourceUser?.availability?.isAvailable ?? true,
});

const CV_ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
const CV_MAX_BYTES = 2 * 1024 * 1024; // 2 MB

const blankExperience = () => ({
  title: '',
  company: '',
  location: '',
  startDate: '',
  endDate: '',
  currentlyWorking: false,
  description: '',
});

const buildCareerFormData = (workerProfile) => ({
  isGraduate: workerProfile?.isGraduate ?? false,
  cv: workerProfile?.cv || null,
  workExperience: Array.isArray(workerProfile?.workExperience)
    ? workerProfile.workExperience.map((exp) => ({
        title: exp.title || '',
        company: exp.company || '',
        location: exp.location || '',
        startDate: exp.startDate || '',
        endDate: exp.endDate || '',
        currentlyWorking: Boolean(exp.currentlyWorking),
        description: exp.description || '',
      }))
    : [],
  razorpayAccountId: workerProfile?.razorpayAccountId || '',
});

const parseSkillsList = (skills) =>
  String(skills || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

const buildWorkerPayload = (form) => {
  const experiences = (form?.workExperience || [])
    .map((exp) => ({
      title: exp.title?.trim() || '',
      company: exp.company?.trim() || '',
      location: exp.location?.trim() || '',
      startDate: exp.startDate?.trim() || '',
      endDate: exp.currentlyWorking ? 'Present' : exp.endDate?.trim() || '',
      currentlyWorking: Boolean(exp.currentlyWorking),
      description: exp.description?.trim() || '',
    }))
    .filter((exp) => exp.title || exp.company || exp.description);

  return {
    skills: parseSkillsList(form?.skills),
    isGraduate: Boolean(form?.isGraduate),
    workExperience: experiences,
    cv: form?.isGraduate ? form?.cv || null : null,
    razorpayAccountId: form?.razorpayAccountId?.trim() || '',
  };
};

/* ── Glass card wrapper ── */
const GlassCard = ({ children, className = '', style = {} }) => (
  <div
    className={`rounded-2xl border overflow-hidden ${className}`}
    style={{
      background: 'rgba(255,255,255,0.06)',
      backdropFilter: 'blur(18px)',
      WebkitBackdropFilter: 'blur(18px)',
      borderColor: 'rgba(255,255,255,0.12)',
      ...style,
    }}
  >
    {children}
  </div>
);

const GlassHeader = ({ title, action }) => (
  <header
    className="flex items-center justify-between px-6 py-4 border-b"
    style={{
      borderColor: 'rgba(255,255,255,0.1)',
      background: 'rgba(255,255,255,0.04)',
    }}
  >
    <h2 className="text-xs uppercase tracking-wider font-bold" style={{ color: 'var(--foreground)' }}>
      {title}
    </h2>
    {action}
  </header>
);

const FieldRow = ({ label, children, last = false }) => (
  <div
    className={`grid sm:grid-cols-[160px_1fr] gap-3 sm:gap-6 py-3 items-start ${last ? '' : 'border-b'}`}
    style={{ borderColor: 'rgba(255,255,255,0.08)' }}
  >
    <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--muted-foreground)' }}>
      {label}
    </p>
    <div className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>
      {children}
    </div>
  </div>
);

/* ── Sidebar Nav Item ── */
const SidebarItem = ({ icon: Icon, label, active = false, onClick, danger = false }) => (
  <button
    onClick={onClick}
    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all duration-200 ${
      active
        ? 'bg-purple-500/20 text-purple-400'
        : danger
        ? 'text-red-400 hover:bg-red-500/10'
        : 'hover:bg-white/5'
    }`}
    style={{ color: active ? undefined : danger ? undefined : 'var(--foreground)' }}
  >
    <Icon className="w-4 h-4 flex-shrink-0" />
    <span>{label}</span>
    {active && <ChevronRight className="w-4 h-4 ml-auto" />}
  </button>
);


const Profile = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, loading: authLoading, loginWithData, logout } = useAuth();
  const { isDark } = useTheme();
  const avatarInputRef = useRef(null);
  const cvInputRef = useRef(null);

  const bgImage = isDark ? '/images/team/dark.png' : '/images/team/light.avif';
  const overlayBg = isDark ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)';
  const textColor = isDark ? 'text-white' : 'text-black';
  const localVars = isDark ? {
    '--foreground': '#ffffff',
    '--muted-foreground': '#a1a1aa',
    '--card-foreground': '#ffffff',
    '--border': 'rgba(255, 255, 255, 0.15)',
    '--accent': 'rgba(255, 255, 255, 0.08)',
    '--popover': '#18181b',
    '--popover-foreground': '#ffffff',
    '--muted': 'rgba(255, 255, 255, 0.05)',
  } : {
    '--foreground': '#000000',
    '--muted-foreground': '#475569',
    '--card-foreground': '#000000',
    '--border': 'rgba(0, 0, 0, 0.1)',
    '--accent': 'rgba(0, 0, 0, 0.05)',
    '--popover': '#ffffff',
    '--popover-foreground': '#000000',
    '--muted': 'rgba(0, 0, 0, 0.02)',
  };

  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState('');
  const [formData, setFormData] = useState(buildFormData(null));
  const [workerProfile, setWorkerProfile] = useState(null);
  const [workerForm, setWorkerForm] = useState(buildCareerFormData(null));
  const [loadingWorkerProfile, setLoadingWorkerProfile] = useState(false);
  const [workerError, setWorkerError] = useState('');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (user) {
      setFormData(buildFormData(user));
    }
  }, [user]);

  useEffect(() => {
    if (!user || user.role !== 'worker') {
      setWorkerProfile(null);
      setWorkerForm(buildCareerFormData(null));
      return;
    }

    let ignore = false;
    const loadWorkerProfile = async () => {
      setLoadingWorkerProfile(true);
      setWorkerError('');
      try {
        const data = await fetchWorkerProfile();
        if (!ignore) {
          setWorkerProfile(data);
          setWorkerForm(buildCareerFormData(data));
        }
      } catch (err) {
        if (!ignore) {
          setWorkerError(err.response?.data?.message || 'Failed to load worker profile.');
        }
      } finally {
        if (!ignore) {
          setLoadingWorkerProfile(false);
        }
      }
    };
    loadWorkerProfile();

    return () => {
      ignore = true;
    };
  }, [user]);

  useEffect(() => {
    if (!saveMsg.includes('successfully')) return;
    const timeoutId = setTimeout(() => setSaveMsg(''), 2500);
    return () => clearTimeout(timeoutId);
  }, [saveMsg]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleGraduateStatus = () => {
    setWorkerForm((prev) => ({
      ...prev,
      isGraduate: !prev.isGraduate,
      cv: prev.isGraduate ? null : prev.cv,
    }));
  };

  const handleExperienceChange = (index, field, value) => {
    setWorkerForm((prev) => {
      const nextExperiences = [...(prev.workExperience || [])];
      nextExperiences[index] = {
        ...nextExperiences[index],
        [field]: field === 'currentlyWorking' ? Boolean(value) : value,
      };
      if (field === 'currentlyWorking' && value) {
        nextExperiences[index].endDate = '';
      }
      return { ...prev, workExperience: nextExperiences };
    });
  };

  const handleAddExperience = () => {
    setWorkerForm((prev) => ({
      ...prev,
      workExperience: [...(prev.workExperience || []), blankExperience()],
    }));
  };

  const handleRemoveExperience = (index) => {
    setWorkerForm((prev) => {
      const nextExperiences = [...(prev.workExperience || [])];
      nextExperiences.splice(index, 1);
      return { ...prev, workExperience: nextExperiences };
    });
  };

  const handleCvInputChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!CV_ALLOWED_TYPES.includes(file.type)) {
      setSaveMsg('Only PDF or Word documents are supported for CV upload.');
      event.target.value = '';
      return;
    }

    if (file.size > CV_MAX_BYTES) {
      setSaveMsg('CV file size must be under 2 MB.');
      event.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setWorkerForm((prev) => ({
        ...prev,
        cv: {
          fileName: file.name,
          fileType: file.type,
          fileSize: file.size,
          fileData: reader.result,
          uploadedAt: new Date().toISOString(),
        },
      }));
    };
    reader.readAsDataURL(file);
    event.target.value = '';
  };

  const handleCvButtonClick = () => {
    if (!workerForm.isGraduate) {
      setSaveMsg('Enable graduate status to upload a CV.');
      return;
    }
    if (!editMode) setEditMode(true);
    setSaveMsg('');
    cvInputRef.current?.click();
  };

  const handleCvRemove = () => {
    setWorkerForm((prev) => ({ ...prev, cv: null }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setSaveMsg('Please choose a valid image file.');
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setSaveMsg('');
      setFormData((prev) => ({ ...prev, profileImage: reader.result }));
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleAvatarButtonClick = () => {
    if (!editMode) setEditMode(true);
    setSaveMsg('');
    avatarInputRef.current?.click();
  };

  const handleCancel = () => {
    if (user) setFormData(buildFormData(user));
    if (workerProfile) setWorkerForm(buildCareerFormData(workerProfile));
    else setWorkerForm(buildCareerFormData(null));
    setEditMode(false);
    setSaveMsg('');
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setSaveMsg('Full name is required.');
      return;
    }

    setSaving(true);
    setSaveMsg('');
    const isWorker = user?.role === 'worker';
    const workerPayload = isWorker ? buildWorkerPayload(workerForm) : null;

    try {
      const payload = {
        name: formData.name.trim(),
        phone: formData.phone.trim(),
        profileImage: formData.profileImage || '',
        bio: formData.bio.trim(),
        skills: formData.skills
          .split(',')
          .map((item) => item.trim())
          .filter(Boolean),
        location: {
          city: formData.city.trim(),
          state: formData.state.trim(),
          pincode: formData.pincode.trim(),
        },
        availability: {
          ...(user?.availability || {}),
          isAvailable: formData.isAvailable,
        },
      };

      const res = await updateProfile(payload);
      loginWithData({ ...res.data, token: localStorage.getItem('token') });

      if (isWorker) {
        const updatedWorker = await updateWorkerCareer(workerPayload);
        setWorkerProfile(updatedWorker);
        setWorkerForm(buildCareerFormData(updatedWorker));
      }

      setSaveMsg('Profile updated successfully.');
      setEditMode(false);
    } catch (err) {
      setSaveMsg(err.response?.data?.message || 'Failed to update profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (authLoading || !user) return null;

  const initials = user.name?.split(' ').map((part) => part[0]).join('').toUpperCase().slice(0, 2) || '?';
  const roleLabel = user.role ? `${user.role.charAt(0).toUpperCase()}${user.role.slice(1)}` : 'Member';
  const rating = Number(user.ratings?.average || 0);
  const completedJobs = Number(user.completedJobs || 0);
  const availabilityText = user.role === 'provider'
    ? (formData.isAvailable ? 'Hiring workers' : 'Not hiring currently')
    : (formData.isAvailable ? 'Available for work' : 'Busy');

  const skillsList = formData.skills.split(',').map((item) => item.trim()).filter(Boolean);
  const cityLocation = [formData.city, formData.state].filter(Boolean).join(', ') || 'Not added';
  const memberSince = user.createdAt ? new Date(user.createdAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A';


  return (
    <div
      className={`min-h-screen pt-20 pb-14 font-sans ${textColor}`}
      style={{
        backgroundImage: `url(${bgImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        ...localVars
      }}
    >
      {/* Overlay for readability */}
      <div className="fixed inset-0 z-0" style={{ background: overlayBg }} />

      <div className="section-container relative z-10">
        <div className="flex gap-6">

          {/* ── LEFT SIDEBAR (Dashboard Nav) ── */}
          <motion.aside
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="hidden lg:block w-64 flex-shrink-0"
          >
            <GlassCard className="sticky top-24">
              {/* Brand */}
              <div className="px-5 py-5 border-b" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center font-bold text-sm"
                    style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}>
                    M
                  </div>
                  <span className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>MattersUrSkills</span>
                </div>
              </div>

              {/* Navigation */}
              <nav className="p-3 space-y-1">
                <SidebarItem icon={User} label="Profile" active />
                {user.role === 'worker' && (
                  <>
                    <SidebarItem icon={Briefcase} label="Find Work" onClick={() => navigate('/worker')} />
                    <SidebarItem icon={FileText} label="My Applications" onClick={() => navigate('/work-history')} />
                  </>
                )}
                {user.role === 'provider' && (
                  <>
                    <SidebarItem icon={Briefcase} label="Post Work" onClick={() => navigate('/provider')} />
                    <SidebarItem icon={Eye} label="View Applicants" onClick={() => navigate('/applicants')} />
                  </>
                )}
                <SidebarItem icon={Settings} label="Settings" onClick={() => {}} />
              </nav>

              {/* Logout */}
              <div className="p-3 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
                <SidebarItem icon={LogOut} label="Logout" danger onClick={handleLogout} />
              </div>
            </GlassCard>
          </motion.aside>


          {/* ── MAIN CONTENT ── */}
          <motion.div initial="hidden" animate="show" variants={containerVariants} className="flex-1 min-w-0 space-y-6">

            {/* ── Profile Overview Card ── */}
            <motion.div variants={cardVariants}>
              <GlassCard>
                <div className="relative flex flex-col md:flex-row md:items-center gap-6 p-6 sm:p-8">

                  {/* Avatar */}
                  <div className="relative w-28 h-28 flex-shrink-0 mx-auto md:mx-0">
                    <div
                      className="w-full h-full rounded-full overflow-hidden border-3 flex items-center justify-center"
                      style={{ borderColor: 'var(--primary)', background: 'rgba(255,255,255,0.1)' }}
                    >
                      {formData.profileImage ? (
                        <img src={formData.profileImage} alt={formData.name || 'Profile'} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-3xl font-extrabold" style={{ color: 'var(--foreground)' }}>{initials}</span>
                      )}
                    </div>

                    <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarChange} className="hidden" />
                    <button
                      type="button"
                      onClick={handleAvatarButtonClick}
                      className="absolute -bottom-1 -right-1 inline-flex h-9 w-9 items-center justify-center rounded-full border shadow-sm transition-colors hover:opacity-90"
                      style={{
                        borderColor: 'rgba(255,255,255,0.15)',
                        background: 'rgba(255,255,255,0.1)',
                        backdropFilter: 'blur(10px)',
                        color: 'var(--foreground)',
                      }}
                    >
                      <Edit3 className="h-4 w-4" />
                    </button>
                  </div>

                  {/* Identity */}
                  <div className="flex-grow text-center md:text-left">
                    <div className="flex items-center justify-center md:justify-start gap-3 mb-1">
                      <h1 className="text-2xl font-bold tracking-tight" style={{ color: 'var(--foreground)' }}>
                        {formData.name || 'Profile'}
                      </h1>
                      <Shield className="w-5 h-5 text-blue-400" />
                    </div>
                    <p className="text-sm font-medium text-purple-400">{roleLabel}</p>
                    <p className="text-xs mt-1 flex items-center justify-center md:justify-start gap-1" style={{ color: 'var(--muted-foreground)' }}>
                      <MapPin className="w-3 h-3" /> {cityLocation}
                    </p>
                    <p className="text-xs mt-0.5 flex items-center justify-center md:justify-start gap-1"
                      style={{ color: formData.isAvailable ? '#10b981' : '#f59e0b' }}>
                      ● {availabilityText}
                    </p>

                    {/* Bio */}
                    {formData.bio && (
                      <p className="text-sm mt-3 leading-relaxed max-w-lg" style={{ color: 'var(--muted-foreground)' }}>
                        {formData.bio}
                      </p>
                    )}

                    {/* Skills tags */}
                    {skillsList.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3 justify-center md:justify-start">
                        {skillsList.slice(0, 6).map((skill) => (
                          <span key={skill} className="text-xs px-3 py-1 rounded-full font-medium"
                            style={{
                              background: 'rgba(168,85,247,0.15)',
                              color: '#c084fc',
                              border: '1px solid rgba(168,85,247,0.2)',
                            }}>
                            {skill}
                          </span>
                        ))}
                        {skillsList.length > 6 && (
                          <span className="text-xs px-3 py-1 rounded-full font-medium"
                            style={{ background: 'rgba(255,255,255,0.08)', color: 'var(--muted-foreground)' }}>
                            +{skillsList.length - 6}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-center gap-3 flex-shrink-0">
                    {!editMode ? (
                      <button
                        onClick={() => { setEditMode(true); setSaveMsg(''); }}
                        className="btn-primary text-sm py-2.5 px-5 flex items-center gap-2"
                        style={{ display: user.role === 'admin' ? 'none' : 'flex' }}
                      >
                        <Edit3 className="w-4 h-4" /> Edit Profile
                      </button>
                    ) : (
                      <>
                        <button onClick={handleSave} disabled={saving} className="btn-primary text-sm py-2 px-4">
                          {saving ? 'Saving...' : 'Save'}
                        </button>
                        <button onClick={handleCancel} className="btn-outline text-sm py-2 px-4">
                          Cancel
                        </button>
                      </>
                    )}
                    <button onClick={handleLogout} className="lg:hidden p-2.5 rounded-xl border transition-colors"
                      style={{ color: '#ef4444', borderColor: 'rgba(255,255,255,0.1)', background: 'rgba(255,255,255,0.05)' }}>
                      <LogOut className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {saveMsg && (
                  <div className="px-6 pb-4 text-center md:text-left">
                    <p className={`text-xs font-semibold px-3 py-2 rounded-xl border inline-block`}
                      style={{
                        background: saveMsg.includes('successfully') ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                        borderColor: saveMsg.includes('successfully') ? 'rgba(16, 185, 129, 0.2)' : 'rgba(239, 68, 68, 0.2)',
                        color: saveMsg.includes('successfully') ? '#10b981' : '#ef4444'
                      }}>
                      {saveMsg}
                    </p>
                  </div>
                )}
              </GlassCard>
            </motion.div>


            {/* ── Content Grid: Main + Right sidebar ── */}
            <div className="grid xl:grid-cols-3 gap-6">

              {/* Left/Center Column */}
              <div className="xl:col-span-2 space-y-6">

                {/* Account Details */}
                <motion.div variants={cardVariants}>
                  <GlassCard>
                    <GlassHeader title="Account Details" />
                    <div className="px-6 py-5 space-y-0">
                      {[
                        { icon: User, label: 'Full Name', value: editMode ? (
                          <input value={formData.name} onChange={(e) => handleInputChange('name', e.target.value)}
                            className="input-field" placeholder="Enter full name" />
                        ) : formData.name || 'Not added' },
                        { icon: Mail, label: 'Email', value: user.email || 'Not added' },
                        { icon: Phone, label: 'Phone', value: editMode ? (
                          <input value={formData.phone} onChange={(e) => handleInputChange('phone', e.target.value)}
                            className="input-field" placeholder="+91 98765 43210" />
                        ) : formData.phone || 'Not added' },
                        { icon: MapPin, label: 'Location', value: editMode ? (
                          <div className="grid sm:grid-cols-2 gap-3">
                            <input value={formData.city} onChange={(e) => handleInputChange('city', e.target.value)}
                              className="input-field" placeholder="City" />
                            <input value={formData.state} onChange={(e) => handleInputChange('state', e.target.value)}
                              className="input-field" placeholder="State" />
                          </div>
                        ) : cityLocation },
                        { icon: Calendar, label: 'Member Since', value: memberSince },
                        { icon: Award, label: 'Account Type', value: roleLabel },
                      ].map(({ icon: Icon, label, value }, i, arr) => (
                        <div key={label} className={`flex items-start gap-4 py-3.5 ${i < arr.length - 1 ? 'border-b' : ''}`}
                          style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                          <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ background: 'rgba(168,85,247,0.12)' }}>
                            <Icon className="w-4 h-4 text-purple-400" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[10px] uppercase tracking-wider font-bold mb-1" style={{ color: 'var(--muted-foreground)' }}>{label}</p>
                            <div className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{value}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </GlassCard>
                </motion.div>

                {/* About Me */}
                {user.role !== 'admin' && (
                  <motion.div variants={cardVariants}>
                    <GlassCard>
                      <GlassHeader title="About Me" />
                      <div className="px-6 py-5">
                        {editMode ? (
                          <textarea rows={3} value={formData.bio} onChange={(e) => handleInputChange('bio', e.target.value)}
                            className="input-field resize-none" placeholder="Write a short summary about yourself..." />
                        ) : (
                          <p className="text-sm leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>
                            {formData.bio || 'No bio added yet. Click Edit Profile to add one.'}
                          </p>
                        )}
                      </div>
                    </GlassCard>
                  </motion.div>
                )}

                {/* Skills */}
                {user.role !== 'admin' && (
                  <motion.div variants={cardVariants}>
                    <GlassCard>
                      <GlassHeader title="Skills" />
                      <div className="px-6 py-5">
                        {editMode ? (
                          <div>
                            <input value={formData.skills} onChange={(e) => handleInputChange('skills', e.target.value)}
                              className="input-field" placeholder="Plumbing, Carpentry, Electrical" />
                            <p className="text-[10px] mt-1.5" style={{ color: 'var(--muted-foreground)' }}>Use commas to separate each skill.</p>
                          </div>
                        ) : skillsList.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {skillsList.map((skill) => (
                              <span key={skill} className="text-xs px-3.5 py-1.5 rounded-full font-medium"
                                style={{
                                  background: 'rgba(168,85,247,0.12)',
                                  color: '#c084fc',
                                  border: '1px solid rgba(168,85,247,0.2)',
                                }}>
                                {skill}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>No skills added yet</p>
                        )}
                      </div>
                    </GlassCard>
                  </motion.div>
                )}

                {/* Pincode */}
                {user.role !== 'admin' && editMode && (
                  <motion.div variants={cardVariants}>
                    <GlassCard>
                      <GlassHeader title="Pincode" />
                      <div className="px-6 py-5">
                        <input value={formData.pincode} onChange={(e) => handleInputChange('pincode', e.target.value)}
                          className="input-field" placeholder="Pincode" />
                      </div>
                    </GlassCard>
                  </motion.div>
                )}

                {/* Career Portfolio (Worker Only) */}
                {user.role === 'worker' && (
                  <motion.div variants={cardVariants}>
                    <GlassCard>
                      <GlassHeader title="Career Portfolio" />
                      <div className="px-6 py-5 space-y-4">
                        <input ref={cvInputRef} type="file" accept={CV_ALLOWED_TYPES.join(',')} onChange={handleCvInputChange} className="hidden" />

                        {workerError && <p className="text-xs text-red-500 mb-3">{workerError}</p>}

                        {loadingWorkerProfile ? (
                          <div className="py-6 text-sm" style={{ color: 'var(--muted-foreground)' }}>Loading career data...</div>
                        ) : (
                          <>
                            <FieldRow label="Graduate Status">
                              {editMode ? (
                                <button type="button" onClick={toggleGraduateStatus}
                                  className={`inline-flex items-center rounded-xl px-3 py-1.5 text-xs font-semibold border transition-all ${
                                    workerForm.isGraduate
                                      ? 'bg-purple-500/20 text-purple-400 border-purple-500/30'
                                      : 'bg-transparent border-white/10'
                                  }`}
                                  style={{ color: workerForm.isGraduate ? undefined : 'var(--foreground)' }}>
                                  {workerForm.isGraduate ? 'Graduate (CV enabled)' : 'Non-graduate'}
                                </button>
                              ) : (
                                <span>{workerForm.isGraduate ? 'Graduate' : 'Non-graduate'}</span>
                              )}
                            </FieldRow>

                            <FieldRow label="Resume / CV">
                              {!workerForm.isGraduate ? (
                                <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                                  <p>Mark yourself as a graduate to enable CV uploads.</p>
                                </div>
                              ) : workerForm.cv ? (
                                <div className="space-y-3">
                                  <div className="flex items-start gap-2.5 p-3 rounded-xl border"
                                    style={{ background: 'rgba(255,255,255,0.05)', borderColor: 'rgba(255,255,255,0.1)' }}>
                                    <FileText className="w-5 h-5 flex-shrink-0" style={{ color: 'var(--muted-foreground)' }} />
                                    <div className="min-w-0">
                                      <p className="text-xs font-bold truncate" style={{ color: 'var(--foreground)' }}>{workerForm.cv.fileName}</p>
                                      <p className="text-[10px]" style={{ color: 'var(--muted-foreground)' }}>
                                        {workerForm.cv.fileSize ? `${(workerForm.cv.fileSize / 1024).toFixed(0)} KB` : ''}
                                        {workerForm.cv.uploadedAt ? ` · ${new Date(workerForm.cv.uploadedAt).toLocaleDateString()}` : ''}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="flex gap-2">
                                    <a href={workerForm.cv.fileData} download={workerForm.cv.fileName || 'resume.pdf'} className="btn-outline text-xs !py-1.5 !px-3">
                                      Download
                                    </a>
                                    <button type="button" onClick={handleCvButtonClick} className="btn-primary text-xs !py-1.5 !px-3">
                                      Replace
                                    </button>
                                    {editMode && (
                                      <button type="button" onClick={handleCvRemove} className="btn-ghost text-xs !py-1.5 !px-2" style={{ color: '#ef4444' }}>
                                        Remove
                                      </button>
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                                  <p>No CV uploaded yet. (PDF or Word, max 2MB)</p>
                                  <button type="button" onClick={handleCvButtonClick} className="btn-outline text-xs mt-2.5 !py-1.5 !px-3">
                                    Upload Resume
                                  </button>
                                </div>
                              )}
                            </FieldRow>

                            <FieldRow label="Experience">
                              {workerForm.workExperience.length > 0 ? (
                                <div className="space-y-4">
                                  {workerForm.workExperience.map((exp, index) => (
                                    <div key={index} className="rounded-xl border p-4 space-y-3"
                                      style={{ background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)' }}>
                                      {editMode ? (
                                        <div className="space-y-3">
                                          <input value={exp.title} onChange={(e) => handleExperienceChange(index, 'title', e.target.value)} className="input-field" placeholder="Role/Position" />
                                          <input value={exp.company} onChange={(e) => handleExperienceChange(index, 'company', e.target.value)} className="input-field" placeholder="Company/Client" />
                                          <div className="grid grid-cols-2 gap-3">
                                            <input value={exp.startDate} onChange={(e) => handleExperienceChange(index, 'startDate', e.target.value)} className="input-field" placeholder="Start Date" />
                                            <input value={exp.endDate} onChange={(e) => handleExperienceChange(index, 'endDate', e.target.value)} className="input-field" placeholder="End Date" disabled={exp.currentlyWorking} />
                                          </div>
                                          <textarea rows={2} value={exp.description} onChange={(e) => handleExperienceChange(index, 'description', e.target.value)} className="input-field resize-none" placeholder="Description/Achievements" />
                                          <div className="flex justify-between items-center pt-2">
                                            <label className="flex items-center gap-2 cursor-pointer">
                                              <input type="checkbox" checked={exp.currentlyWorking} onChange={(e) => handleExperienceChange(index, 'currentlyWorking', e.target.checked)} className="w-3.5 h-3.5 accent-purple-500" />
                                              <span className="text-xs" style={{ color: 'var(--foreground)' }}>Current role</span>
                                            </label>
                                            <button type="button" onClick={() => handleRemoveExperience(index)} className="text-xs" style={{ color: '#ef4444' }}>Remove</button>
                                          </div>
                                        </div>
                                      ) : (
                                        <div>
                                          <h4 className="font-bold text-xs" style={{ color: 'var(--foreground)' }}>{exp.title}</h4>
                                          <p className="text-[11px]" style={{ color: 'var(--muted-foreground)' }}>{exp.company} {exp.location ? `· ${exp.location}` : ''}</p>
                                          <p className="text-[10px] font-semibold mt-1" style={{ color: 'var(--muted-foreground)' }}>{exp.startDate} — {exp.currentlyWorking ? 'Present' : exp.endDate}</p>
                                          {exp.description && <p className="text-xs mt-2 leading-relaxed" style={{ color: 'var(--muted-foreground)' }}>{exp.description}</p>}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                  {editMode && (
                                    <button type="button" onClick={handleAddExperience} className="btn-outline text-xs !py-1.5 !px-3">
                                      Add Experience
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                                  <p>No experience listed.</p>
                                  {editMode && (
                                    <button type="button" onClick={handleAddExperience} className="btn-outline text-xs mt-2.5 !py-1.5 !px-3">
                                      Add Experience
                                    </button>
                                  )}
                                </div>
                              )}
                            </FieldRow>

                            <FieldRow label="Linked Bank Account" last>
                              {editMode ? (
                                <div>
                                  <input value={workerForm.razorpayAccountId} onChange={(e) => setWorkerForm(prev => ({ ...prev, razorpayAccountId: e.target.value }))}
                                    className="input-field" placeholder="acc_XXXXXXXXXXXXXXXX" />
                                  <p className="text-[10px] mt-1" style={{ color: 'var(--muted-foreground)' }}>Enter your Razorpay Linked Account ID to receive payments directly.</p>
                                </div>
                              ) : (
                                <span>{workerForm.razorpayAccountId || 'Not set up'}</span>
                              )}
                            </FieldRow>
                          </>
                        )}
                      </div>
                    </GlassCard>
                  </motion.div>
                )}

                {/* Provider: View Applicants */}
                {user.role === 'provider' && (
                  <motion.div variants={cardVariants}>
                    <GlassCard>
                      <div className="px-6 py-5">
                        <button onClick={() => navigate('/applicants')} className="btn-primary w-full text-sm py-3 flex items-center justify-center gap-2">
                          <Eye className="w-4 h-4" /> View Applicants Details
                        </button>
                      </div>
                    </GlassCard>
                  </motion.div>
                )}
              </div>


              {/* ── Right Column (Stats) ── */}
              <div className="space-y-6">

                {/* Activity Summary */}
                {user.role !== 'admin' && (
                  <motion.div variants={cardVariants}>
                    <GlassCard>
                      <GlassHeader title="Activity Summary" action={
                        user.role === 'worker' ? (
                          <button onClick={() => navigate('/work-history')} className="text-xs font-semibold text-purple-400 hover:text-purple-300 transition-colors">
                            View All
                          </button>
                        ) : null
                      } />
                      <div className="px-6 py-5 space-y-0">
                        {[
                          { icon: Briefcase, label: 'Jobs Completed', value: completedJobs, color: '#10b981' },
                          { icon: Star, label: 'Rating', value: `${rating.toFixed(1)} / 5.0`, color: '#f59e0b' },
                          { icon: Eye, label: 'Profile Views', value: user.profileViews || 0, color: '#f43f5e' },
                          { icon: Send, label: 'Proposals Sent', value: user.proposalsSent || 0, color: '#3b82f6' },
                        ].map(({ icon: Icon, label, value, color }, i, arr) => (
                          <div key={label} className={`flex items-center justify-between py-3.5 ${i < arr.length - 1 ? 'border-b' : ''}`}
                            style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                                style={{ background: `${color}15` }}>
                                <Icon className="w-4 h-4" style={{ color }} />
                              </div>
                              <span className="text-xs font-medium" style={{ color: 'var(--muted-foreground)' }}>{label}</span>
                            </div>
                            <span className="text-sm font-bold" style={{ color: 'var(--foreground)' }}>{value}</span>
                          </div>
                        ))}
                      </div>
                    </GlassCard>
                  </motion.div>
                )}

                {/* Contact Info */}
                <motion.div variants={cardVariants}>
                  <GlassCard>
                    <GlassHeader title="Contact Info" />
                    <div className="px-6 py-5 space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(168,85,247,0.12)' }}>
                          <Phone className="w-4 h-4 text-purple-400" />
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--muted-foreground)' }}>Phone</p>
                          {formData.phone ? (
                            <a href={`tel:${formData.phone}`} className="text-sm font-medium hover:underline" style={{ color: 'var(--foreground)' }}>{formData.phone}</a>
                          ) : (
                            <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Not added</span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(168,85,247,0.12)' }}>
                          <Mail className="w-4 h-4 text-purple-400" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--muted-foreground)' }}>Email</p>
                          <a href={`mailto:${user.email}`} className="text-sm font-medium hover:underline break-all" style={{ color: 'var(--foreground)' }}>{user.email}</a>
                        </div>
                      </div>
                    </div>
                  </GlassCard>
                </motion.div>

                {/* Availability Toggle (in right sidebar when editing) */}
                {user.role !== 'admin' && editMode && (
                  <motion.div variants={cardVariants}>
                    <GlassCard>
                      <GlassHeader title="Availability" />
                      <div className="px-6 py-5">
                        <button type="button" onClick={() => handleInputChange('isAvailable', !formData.isAvailable)}
                          className={`inline-flex items-center rounded-xl px-4 py-2 text-xs font-semibold border transition-all ${
                            formData.isAvailable ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/25' : 'bg-amber-500/15 text-amber-400 border-amber-500/25'
                          }`}>
                          {formData.isAvailable ? '● Active — Available for work' : '● Busy — Not available'}
                        </button>
                      </div>
                    </GlassCard>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Profile;